// Adaptive quality: watches the frame rate and steps shadows, resolution and crowd size down when a
// device cannot keep up. Only ever steps down within a session; the level reached is remembered so the
// next session on that device starts there.

import type { GameEngine } from './engine';

export type QualityLevel = 0 | 1 | 2 | 3; // 3 high .. 0 lowest
export interface QualityHooks { setCrowd(peds: number, traffic: number, peopleRange: number): void; }

const KEY = 'cab.quality';

export class QualityManager {
  level: QualityLevel;
  private samples: number[] = [];
  private lastChange = performance.now();
  constructor(readonly g: GameEngine, readonly hooks: QualityHooks, readonly force: QualityLevel | null = null) {
    let saved: QualityLevel = 3;
    try { const v = Number(localStorage.getItem(KEY)); if (v >= 0 && v <= 3) saved = v as QualityLevel; } catch { /* ignore */ }
    this.level = force ?? saved;
    this.apply();
  }

  /** Call once per rendered frame. */
  tick(): void {
    if (this.force != null) return;
    const fps = this.g.engine.getFps();
    this.samples.push(fps); if (this.samples.length > 90) this.samples.shift();
    if (this.samples.length < 90 || performance.now() - this.lastChange < 6000) return;
    const avg = this.samples.reduce((a, b) => a + b, 0) / this.samples.length;
    const save = () => { try { localStorage.setItem(KEY, String(this.level)); } catch { /* ignore */ } };
    if (avg < 30 && this.level > 0) { this.level = (this.level - 1) as QualityLevel; this.apply(); this.samples = []; this.lastChange = performance.now(); save(); console.log(`[cab] quality down to ${this.level} (avg ${avg.toFixed(0)} fps)`); }
    // a device that comfortably holds 58+ fps for a while can try one step up again (so a one-off stall
    // during loading does not pin it low forever); at most one step up per 20 s
    else if (avg > 58 && this.level < 3 && performance.now() - this.lastChange > 20000) { this.level = (this.level + 1) as QualityLevel; this.apply(); this.samples = []; this.lastChange = performance.now(); save(); console.log(`[cab] quality up to ${this.level} (avg ${avg.toFixed(0)} fps)`); }
  }

  apply(): void {
    const { engine, shadows } = this.g; const dpr = window.devicePixelRatio || 1;
    const scale = [1.5, 1.25, 1, 1][this.level]; // hardware scaling: larger = fewer pixels
    engine.setHardwareScalingLevel(Math.max(1 / Math.min(dpr, 2), scale === 1 ? 1 / Math.min(dpr, 2) : scale / Math.min(dpr, 1.5)));
    const map = shadows.getShadowMap();
    if (this.level === 3) { shadows.numCascades = 2; shadows.shadowMaxZ = 220; shadows.mapSize = 1024; if (map) map.refreshRate = 1; this.g.sun.shadowEnabled = true; }
    else if (this.level === 2) { shadows.numCascades = 1; shadows.shadowMaxZ = 120; shadows.mapSize = 1024; if (map) map.refreshRate = 1; this.g.sun.shadowEnabled = true; }
    else if (this.level === 1) { shadows.numCascades = 1; shadows.shadowMaxZ = 80; shadows.mapSize = 512; if (map) map.refreshRate = 2; this.g.sun.shadowEnabled = true; }
    else { this.g.sun.shadowEnabled = false; }
    this.hooks.setCrowd([20, 35, 50, 70][this.level], [4, 8, 10, 12][this.level], [45, 60, 75, 90][this.level]);
  }

  label(): string { return ['lowest', 'low', 'medium', 'high'][this.level]; }
}
